import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL as API } from '../config/api';

const Newsletter = () => {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const content = {
    fr: {
      title: 'Restez Informé',
      subtitle: "Inscrivez-vous à notre newsletter pour recevoir les dernières actualités du Marathon de l'Avenir EDENLIFE 2026",
      placeholder: 'Votre adresse email',
      subscribe: "S'inscrire",
      success: 'Merci ! Vous êtes inscrit avec succès.',
      alreadySubscribed: 'Vous êtes déjà inscrit !',
      error: 'Une erreur est survenue. Veuillez réessayer.',
      privacy: 'Nous respectons votre vie privée. Désabonnement possible à tout moment.',
      invalidEmail: 'Veuillez entrer un email valide'
    },
    en: {
      title: 'Stay Informed',
      subtitle: 'Subscribe to our newsletter to receive the latest news about the EDENLIFE Marathon 2026',
      placeholder: 'Your email address',
      subscribe: 'Subscribe',
      success: 'Thank you! You have successfully subscribed.',
      alreadySubscribed: 'You are already subscribed!',
      error: 'An error occurred. Please try again.',
      privacy: 'We respect your privacy. Unsubscribe at any time.',
      invalidEmail: 'Please enter a valid email'
    },
    ht: {
      title: 'Rete Enfòme',
      subtitle: 'Enskri nan bilten nou an pou resevwa dènye nouvèl sou Maraton EDENLIFE 2026',
      placeholder: 'Adrès imel ou',
      subscribe: 'Enskri',
      success: 'Mèsi! Ou enskri avèk siksè.',
      alreadySubscribed: 'Ou deja enskri!',
      error: 'Gen yon erè ki pase. Tanpri eseye ankò.',
      privacy: 'Nou respekte vi prive ou. Ou ka dezabòne nenpòt lè.',
      invalidEmail: 'Tanpri antre yon imel valid'
    },
    es: {
      title: 'Mantente Informado',
      subtitle: 'Suscríbete a nuestro boletín para recibir las últimas noticias del Maratón EDENLIFE 2026',
      placeholder: 'Tu correo electrónico',
      subscribe: 'Suscribirse',
      success: '¡Gracias! Te has suscrito con éxito.',
      alreadySubscribed: '¡Ya estás suscrito!',
      error: 'Ha ocurrido un error. Por favor intenta de nuevo.',
      privacy: 'Respetamos tu privacidad. Puedes cancelar la suscripción en cualquier momento.',
      invalidEmail: 'Por favor ingresa un correo válido'
    },
    pt: {
      title: 'Fique Informado',
      subtitle: 'Inscreva-se em nossa newsletter para receber as últimas notícias da Maratona EDENLIFE 2026',
      placeholder: 'Seu endereço de email',
      subscribe: 'Inscrever-se',
      success: 'Obrigado! Você se inscreveu com sucesso.',
      alreadySubscribed: 'Você já está inscrito!',
      error: 'Ocorreu um erro. Por favor tente novamente.',
      privacy: 'Respeitamos sua privacidade. Você pode cancelar a inscrição a qualquer momento.',
      invalidEmail: 'Por favor insira um email válido'
    }
  };

  const t = content[i18n.language] || content.fr;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error(t.invalidEmail);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        language: i18n.language
      });
      
      if (response.data.status === 'existing') {
        toast.info(t.alreadySubscribed);
      } else {
        setSubscribed(true);
        toast.success(t.success);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 px-6 bg-gradient-to-br from-secondary to-accent">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Mail className="w-16 h-16 text-white mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t.title}
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            {t.subtitle}
          </p>

          {subscribed ? (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex items-center justify-center space-x-2 text-white"
            >
              <CheckCircle className="w-8 h-8" />
              <span className="text-xl font-semibold">{t.success}</span>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.placeholder}
                className="flex-1 h-14 text-lg bg-white/90 border-0 rounded-full px-6"
                required
              />
              <Button
                type="submit"
                disabled={loading}
                className="h-14 px-8 bg-primary hover:bg-primary/90 text-white rounded-full text-lg font-semibold"
              >
                <Send className="w-5 h-5 mr-2" />
                {t.subscribe}
              </Button>
            </form>
          )}

          <p className="text-white/60 text-sm mt-6">
            {t.privacy}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;